import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Check } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import HeroBg from "../data/App.jpg";
import BrandLogo from "../../components/BrandLogo";

const G      = "var(--k-green)";
const G_DARK = "var(--k-green-hover)";
const API    = "https://api.komorotel.com";

const PERKS = [
  "Réservation instantanée et sécurisée",
  "Accès aux meilleurs hébergements des Comores",
  "Historique et gestion de vos réservations",
];

export default function Signup() {
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: "", password: "", name: "", surname: "", phone: "" });
  const [errors,   setErrors]   = useState({});
  const [showPwd,  setShowPwd]  = useState(false);
  const [focused,  setFocused]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: "" }));
    if (apiError) setApiError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Prénom requis";
    if (!form.surname.trim()) e.surname = "Nom requis";
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Email invalide";

    // le back valide le numéro avec libphonenumber, ici on filtre juste l'évident
    const digits = form.phone.replace(/\D/g, "");
    if (!form.phone.startsWith("+") || digits.length < 8) {
      e.phone = "Numéro incomplet (ex: +269 333 88 66)";
    }

    if (!form.password || form.password.length < 8) e.password = "8 caractères minimum";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/auth/register`, form, { withCredentials: true });

      // le back peut répondre 200 avec status FAILED (échec envoi email)
      if (res.data?.status === "FAILED") {
        setApiError(res.data.message || "Une erreur est survenue");
        return;
      }

      navigate("/signup/emailverification", {
        state: { email: form.email, userId: res.data?.user?.id },
      });
    } catch (err) {
      setApiError(err.response?.data?.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (name) => ({
    width: "100%", border: `1.5px solid ${errors[name] ? "#ef4444" : focused === name ? G : "#e5e7eb"}`,
    borderRadius: 12, padding: "12px 14px 12px 42px",
    fontSize: 14, outline: "none", fontFamily: "inherit",
    color: "#111827", background: "#fff",
    boxSizing: "border-box", transition: "border-color 0.2s",
  });

  const iconStyle = { position: "absolute", left: 13, top: 15, transition: "color 0.2s" };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>

      {/* ── Panneau gauche ── */}
      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "flex-end" }} className="signup-hero">
        <img src={HeroBg} alt="Comores" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,40,15,0.88) 0%, rgba(10,40,15,0.45) 55%, rgba(10,40,15,0.1) 100%)" }} />
        <div style={{ position: "relative", zIndex: 2, padding: "0 48px 56px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: G, color: "#fff", fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", padding: "5px 14px", borderRadius: 20, marginBottom: 16 }}>
            🇰🇲 KOMOROTEL
          </div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 30, fontWeight: 700, color: "#fff", lineHeight: 1.25, marginBottom: 20 }}>
            Rejoignez la première plateforme hôtelière des Comores
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {PERKS.map((perk, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgb(var(--k-green-rgb)/0.3)", border: "1.5px solid rgb(var(--k-green-rgb)/0.6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Check size={12} color="#fff" strokeWidth={3} />
                </div>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>{perk}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Panneau droit ── */}
      <div style={{ width: 500, background: "#fff", display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 48px", overflowY: "auto" }} className="signup-form-panel">

        <Link to="/" style={{ display: "block", textDecoration: "none", marginBottom: 32 }}>
          <BrandLogo variant="horizontal" height={34} />
        </Link>

        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
          Créer un compte
        </h1>
        <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 28 }}>
          Rejoignez Komorotel en quelques secondes
        </p>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }} noValidate>

          {/* Prénom + Nom */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[{ key: "name", ph: "Prénom *", ac: "given-name" }, { key: "surname", ph: "Nom *", ac: "family-name" }].map(f => (
              <div key={f.key} style={{ position: "relative" }}>
                <User size={15} color={focused === f.key ? G : "#9ca3af"} style={iconStyle} />
                <input
                  type="text" value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                  placeholder={f.ph} autoComplete={f.ac}
                  style={inputStyle(f.key)}
                  onFocus={() => setFocused(f.key)} onBlur={() => setFocused(null)}
                />
                {errors[f.key] && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors[f.key]}</div>}
              </div>
            ))}
          </div>

          {/* Email */}
          <div style={{ position: "relative" }}>
            <Mail size={15} color={focused === "email" ? G : "#9ca3af"} style={iconStyle} />
            <input
              type="email" value={form.email} onChange={e => set("email", e.target.value)}
              placeholder="Adresse email *" autoComplete="email"
              style={inputStyle("email")}
              onFocus={() => setFocused("email")} onBlur={() => setFocused(null)}
            />
            {errors.email && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors.email}</div>}
          </div>

          {/* Téléphone */}
          <div>
            <PhoneInput
              country="km"
              preferredCountries={["km", "fr", "yt", "re"]}
              value={form.phone}
              onChange={phone => set("phone", "+" + phone.replace(/\D/g, ""))}
              inputProps={{ name: "phone", autoComplete: "tel" }}
              inputStyle={{
                width: "100%", height: 46, borderRadius: 12,
                border: `1.5px solid ${errors.phone ? "#ef4444" : "#e5e7eb"}`,
                fontSize: 14, fontFamily: "inherit",
              }}
              buttonStyle={{
                borderTopLeftRadius: 12, borderBottomLeftRadius: 12,
                border: `1.5px solid ${errors.phone ? "#ef4444" : "#e5e7eb"}`,
                borderRight: "none", background: "#f9fafb",
              }}
            />
            {errors.phone && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors.phone}</div>}
          </div>

          {/* Mot de passe */}
          <div style={{ position: "relative" }}>
            <Lock size={15} color={focused === "password" ? G : "#9ca3af"} style={iconStyle} />
            <input
              type={showPwd ? "text" : "password"}
              value={form.password} onChange={e => set("password", e.target.value)}
              placeholder="Mot de passe * (8 caractères min.)" autoComplete="new-password"
              style={{ ...inputStyle("password"), paddingRight: 44 }}
              onFocus={() => setFocused("password")} onBlur={() => setFocused(null)}
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              style={{ position: "absolute", right: 13, top: 15, background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>

            {/* Barre force mdp */}
            {form.password.length > 0 && (
              <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center" }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: form.password.length >= i * 4 ? (i === 3 ? G : i === 2 ? "#f59e0b" : "#ef4444") : "#f3f4f6", transition: "background 0.3s" }} />
                ))}
                <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 4 }}>
                  {form.password.length < 4 ? "Faible" : form.password.length < 8 ? "Moyen" : "Fort"}
                </span>
              </div>
            )}
            {errors.password && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors.password}</div>}
          </div>

          {/* Erreur API */}
          {apiError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#ef4444" }}>
              ⚠️ {apiError}
            </div>
          )}

          {/* Bouton */}
          <button type="submit" disabled={loading}
            style={{
              width: "100%", background: loading ? "#d1d5db" : G,
              color: "#fff", border: "none", borderRadius: 12,
              padding: "14px 0", fontSize: 14, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              transition: "background 0.18s", marginTop: 4,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = G_DARK; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.backgroundColor = G; }}>
            {loading ? "Création en cours..." : <><span>Créer mon compte</span><ArrowRight size={16} /></>}
          </button>

          <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
            En vous inscrivant, vous acceptez nos{" "}
            <Link to="/conditions" style={{ color: G, fontWeight: 600, textDecoration: "none" }}>conditions générales</Link>
          </p>
        </form>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
          <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
          <span style={{ fontSize: 12, color: "#9ca3af" }}>ou</span>
          <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
        </div>

        <p style={{ fontSize: 13, color: "#6b7280", textAlign: "center" }}>
          Déjà un compte ?{" "}
          <Link to="/login" style={{ color: G, fontWeight: 700, textDecoration: "none" }}>Se connecter</Link>
        </p>

        <p style={{ fontSize: 11, color: "#d1d5db", textAlign: "center", marginTop: 24 }}>© 2026 Komorotel · Tous droits réservés</p>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .signup-hero       { display: none !important; }
          .signup-form-panel { width: 100% !important; padding: 32px 24px !important; }
        }
      `}</style>
    </div>
  );
}